import { GlobalMenu, DeleteDialog, CreateUpdatesDialog } from '@components';

import { useToast } from '@hooks';
import { IUpdateRawQueryList } from '@models/Update.model';
import { UpdateStatusType } from '@models/UpdateStatus.model';
import { deleteUpdate } from '@services/queries/updates';
import { changeUpdateStatus } from '@services/queries/updates/changeUpdateStatus/changeUpdateStatus';
import { useContext, useState } from 'react';
import { useQueryClient, useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { useState as useHookState } from '@hookstate/core';

import { NavigationContext } from '@layout/navigation/navigation';

import { InviteUserRole } from '@models/Invite.model';

import { useAppDispatch } from '@store/redux/store';
import {
  addBackUpUpdatedUpdates,
  addDeletedUpdate,
  addUpdatedUpdate,
} from '@store/redux/features/updatesOptimistic/updatesOptimisticSlice';
import { AffectedItemsDialog } from '../Components/AffectedItemsDialog/AffectedItemsDialog';
import { MarkAsCompleteDialog } from '../Components/MarkAsCompleteDialog/MarkAsCompleteDialog';
import { PriorityDialog } from '../Components/PriorityDialog/PriorityDialog';
import { ReAssignDialog } from '../Components/ReAssignDialog/ReAssignDialog';
import { Container } from './PrioritizedMenu.styles';
import { SetDueDateDialog } from '../Components/SetDueDateDialog/SetDueDateDialog';

interface IPrioritizedMenu {
  updateStatus: IUpdateRawQueryList;
}

const PrioritizedMenu = ({ updateStatus }: IPrioritizedMenu) => {
  const params = useParams<{ id: string; equipmentId: string }>();

  const dispatch = useAppDispatch();

  const { toast } = useToast();

  const { role, userName, updatesMtViewTime } = useContext(NavigationContext);

  const queryClient = useQueryClient();

  const openDeleteDialog = useHookState(false);

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openMarkAsCompleteDialog, setOpenMarkAsCompleteDialog] =
    useState<boolean>(false);
  const [openPriorityDialog, setOpenPriorityDialog] = useState<boolean>(false);
  const [openReAssignDialog, setOpenReAssignDialog] = useState<boolean>(false);
  const [openDueDateDialog, setOpenDueDateDialog] = useState<boolean>(false);
  const [openAffectedItemsDialog, setOpenAffectedItemsDialog] =
    useState<boolean>(false);

  const mutation = useMutation(changeUpdateStatus, {
    onSuccess: async newUpdate => {
      dispatch(addBackUpUpdatedUpdates(newUpdate));
      dispatch(addUpdatedUpdate({ newUpdate, updatesMtViewTime }));

      toast.success('Update changed successfullys');
      queryClient.invalidateQueries(['updateStatuses']);
      queryClient.invalidateQueries(['updatesMetrics']);
      queryClient.invalidateQueries(['updateStatus', updateStatus.id]);
      queryClient.invalidateQueries(['studios', params.id, 'updateStatuses']);
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'updateStatuses',
      ]);
    },
    onError: () => {
      toast.error('Something got wrong');
    },
  });

  const mutationDelete = useMutation(deleteUpdate, {
    onSuccess: async () => {
      toast.success('Update was deleted successfully');
      openDeleteDialog.set(false);

      dispatch(
        addDeletedUpdate({ updateId: updateStatus.id, updatesMtViewTime }),
      );

      queryClient.invalidateQueries(['updateStatuses']);
      queryClient.invalidateQueries(['updatesMetrics']);
      queryClient.invalidateQueries(['updateStatus', updateStatus.id]);
      queryClient.invalidateQueries(['studios', params.id, 'updateStatuses']);
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'updateStatuses',
      ]);
    },
    onError: () => {
      toast.error('Something got wrong');
    },
  });

  const disableMarkAsComplete = () => {
    if (
      [InviteUserRole.MEMBER].includes(role) &&
      userName === updateStatus.aName
    )
      return false;
    if ([InviteUserRole.ADMIN, InviteUserRole.EDITOR].includes(role))
      return false;
    return true;
  };

  const disableMarkAsCompleteTooltip = () => {
    if (
      [InviteUserRole.MEMBER].includes(role) &&
      userName === updateStatus.aName
    )
      return true;
    if ([InviteUserRole.ADMIN, InviteUserRole.EDITOR].includes(role))
      return true;
    return false;
  };

  const options = () => {
    const initialOptions = [
      {
        disabled: ![InviteUserRole.ADMIN].includes(role),
        disableToolTip: [InviteUserRole.ADMIN].includes(role),
        toolTipText: 'Only admins can change priorities.',
        toolTipId: 'priorityTooltip',
        label: 'Change Priority',
        iconName: 'flag',
        onClick: () => {
          setOpenPriorityDialog(true);
        },
      },
      {
        disabled: ![InviteUserRole.ADMIN].includes(role),
        disableToolTip: [InviteUserRole.ADMIN].includes(role),
        toolTipText: 'Only admins can assign/re-assign updates.',
        toolTipId: 'assigneeTooltip',
        label: updateStatus.assignee ? 'Re-assign' : 'Assign',
        iconName: 'new-person',
        onClick: () => {
          setOpenReAssignDialog(true);
        },
        bottomSeparator: false,
      },
      {
        disabled: ![InviteUserRole.ADMIN].includes(role),
        disableToolTip: [InviteUserRole.ADMIN].includes(role),
        toolTipText: 'Only admins can set due dates to updates.',
        toolTipId: 'dueDateTooltip',
        label: 'Set Due Date',
        iconName: 'calendar',
        onClick: () => {
          setOpenDueDateDialog(true);
        },
        bottomSeparator: true,
      },
      {
        disabled: disableMarkAsComplete(),
        disableToolTip: disableMarkAsCompleteTooltip(),
        toolTipText:
          'Only admins, editors and users assigned to this update can mark it as complete.',
        toolTipId: 'completeTooltip',
        label: 'Mark as Complete',
        iconName: 'tick-circle',
        onClick: () => {
          setOpenMarkAsCompleteDialog(true);
        },
        bottomSeparator: true,
      },
      {
        label: 'See Affected Assets',
        iconName: 'cube',
        onClick: () => {
          setOpenAffectedItemsDialog(true);
        },
      },
      {
        disabled: ![InviteUserRole.ADMIN].includes(role),
        disableToolTip: [InviteUserRole.ADMIN].includes(role),
        toolTipText: 'Only admins can dismiss updates.',
        toolTipId: 'dismissTooltip',
        label: 'Dismiss',
        iconName: 'delete',
        onClick: () => {
          if (!mutation.isLoading) {
            mutation.mutate({
              updateStatusId: updateStatus.usId,
              type: UpdateStatusType.DISMISSED,
            });
          }
        },
      },
    ];

    if (updateStatus.usStudioId) {
      initialOptions.unshift({
        disabled: ![InviteUserRole.ADMIN].includes(role),
        disableToolTip: [InviteUserRole.ADMIN].includes(role),
        toolTipText: 'Only admins can delete an update.',
        toolTipId: 'deleteTooltip',
        label: 'Delete Update',
        iconName: 'trash',
        onClick: () => {
          openDeleteDialog.set(true);
        },
        bottomSeparator: true,
      });
      initialOptions.unshift({
        disabled: ![InviteUserRole.ADMIN].includes(role),
        disableToolTip: [InviteUserRole.ADMIN].includes(role),
        toolTipText: 'Only admins can edit an update.',
        toolTipId: 'editTooltip',
        label: 'Edit Update',
        iconName: 'edit',
        onClick: () => {
          setOpenEditDialog(true);
        },
      });
    }

    return initialOptions;
  };

  return (
    <Container>
      <GlobalMenu
        align="end"
        hoverColor="#E6E8F0"
        title="Quick Actions"
        itemList={options()}
      />

      {openPriorityDialog && (
        <PriorityDialog
          open={openPriorityDialog}
          onClose={() => {
            setOpenPriorityDialog(false);
          }}
          updateStatus={updateStatus}
          updateId={updateStatus.id}
          updateStatusId={updateStatus.usId}
        />
      )}

      {openEditDialog && (
        <CreateUpdatesDialog
          open={openEditDialog}
          edit={{ updatePrev: updateStatus }}
          onClose={() => setOpenEditDialog(false)}
        />
      )}

      <DeleteDialog
        open={openDeleteDialog}
        onDelete={() => {
          mutationDelete.mutate(updateStatus.id);
        }}
      />

      {openAffectedItemsDialog && (
        <AffectedItemsDialog
          open={openAffectedItemsDialog}
          onClose={() => {
            setOpenAffectedItemsDialog(false);
          }}
          updateId={updateStatus.id}
        />
      )}

      <ReAssignDialog
        open={openReAssignDialog}
        onClose={() => {
          setOpenReAssignDialog(false);
        }}
        assigneeId={updateStatus.assignee}
        updateId={updateStatus.id}
        updateStatusId={updateStatus.usId}
      />

      <MarkAsCompleteDialog
        open={openMarkAsCompleteDialog}
        onClose={() => {
          setOpenMarkAsCompleteDialog(false);
        }}
        updateId={updateStatus.id}
        updateStatusId={updateStatus.usId}
      />

      <SetDueDateDialog
        open={openDueDateDialog}
        onClose={() => {
          setOpenDueDateDialog(false);
        }}
        updateStatus={updateStatus}
        updateId={updateStatus.id}
        updateStatusId={updateStatus.usId}
      />
    </Container>
  );
};

export { PrioritizedMenu };
